import { useState, useEffect } from "react";
import { Lock, Loader2, AlertTriangle, Trash2, Truck, Save, Warehouse } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

import FuelSurchargeSection from "./settings/FuelSurchargeSection";
import EmailDiagnosticsSection from "./settings/EmailDiagnosticsSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSettings() {
  return (
    <div className="max-w-2xl space-y-8">
      <ChangePasswordSection />
      <ExtraDefaultsSection />
      <FuelSurchargeSection />
      <EmailDiagnosticsSection />
      <StorageDefaultsSection />
      <FactoryResetSection />
    </div>
  );
}

function StorageDefaultsSection() {
  const [values, setValues] = useState({ storage_cost_per_pallet: "", in_cost_per_pallet: "", out_cost_per_pallet: "", monthly_insurance_flat: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem("mv_admin_token");
  const h = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    axios.get(`${API}/admin/settings/storage-defaults`, h)
      .then((r) => {
        setValues({
          storage_cost_per_pallet: r.data.storage_cost_per_pallet ?? "",
          in_cost_per_pallet: r.data.in_cost_per_pallet ?? "",
          out_cost_per_pallet: r.data.out_cost_per_pallet ?? "",
          monthly_insurance_flat: r.data.monthly_insurance_flat ?? "",
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings/storage-defaults`, {
        storage_cost_per_pallet: parseFloat(values.storage_cost_per_pallet) || 0,
        in_cost_per_pallet: parseFloat(values.in_cost_per_pallet) || 0,
        out_cost_per_pallet: parseFloat(values.out_cost_per_pallet) || 0,
        monthly_insurance_flat: parseFloat(values.monthly_insurance_flat) || 0,
      }, h);
      toast.success("Tariffe stoccaggio salvate");
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="bg-white border border-zinc-200 rounded-sm p-6" data-testid="section-storage-defaults">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-sm bg-blue-100 flex items-center justify-center flex-shrink-0">
          <Warehouse size={18} className="text-blue-700" />
        </div>
        <div>
          <h3 className="font-heading text-sm font-bold uppercase tracking-widest text-zinc-800">Tariffe Stoccaggio (default)</h3>
          <p className="text-sm text-zinc-500 mt-1">
            Tariffe globali di default per lo stoccaggio bancali. Possono essere sovrascritte sul singolo cliente dalla pagina "Clienti".
            Il costo di stoccaggio mensile si calcola sulla media giornaliera di bancali, misurata via snapshot automatico notturno.
          </p>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-zinc-500"><Loader2 size={14} className="inline animate-spin mr-2" /> Caricamento...</p>
      ) : (
        <div className="space-y-3">
          {[
            { key: "storage_cost_per_pallet", label: "Stoccaggio (€/bancale/mese)", placeholder: "es. 10.00" },
            { key: "in_cost_per_pallet", label: "Handling Ingresso (€/bancale)", placeholder: "es. 4.00" },
            { key: "out_cost_per_pallet", label: "Handling Uscita (€/bancale)", placeholder: "es. 4.00" },
            { key: "monthly_insurance_flat", label: "Assicurazione All Risk (€/mese forfait)", placeholder: "es. 104.16" },
          ].map((f) => (
            <div key={f.key} className="flex items-center justify-between gap-4">
              <label className="text-sm text-zinc-700 font-medium">{f.label}</label>
              <input
                type="number" step="0.01" min="0"
                value={values[f.key]}
                onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                placeholder={f.placeholder}
                className="w-40 h-9 px-3 border border-zinc-300 rounded-sm text-sm focus:border-[#FF5200] outline-none"
                data-testid={`storage-default-${f.key}`}
              />
            </div>
          ))}
          <button
            onClick={save}
            disabled={saving}
            className="mt-2 inline-flex items-center gap-2 bg-blue-700 text-white px-4 py-2 text-sm font-bold uppercase tracking-wide rounded-sm hover:bg-blue-800 disabled:opacity-50"
            data-testid="storage-defaults-save-btn"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Salva Tariffe Stoccaggio
          </button>
        </div>
      )}
    </section>
  );
}

function ExtraDefaultsSection() {
  const [values, setValues] = useState({ sponda_cost: "", camion_cost: "", furgone_cost: "", urgency_cost: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem("mv_admin_token");
  const h = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    axios.get(`${API}/admin/settings/extras-defaults`, h)
      .then((r) => {
        setValues({
          sponda_cost: r.data.sponda_cost || "",
          camion_cost: r.data.camion_cost || "",
          furgone_cost: r.data.furgone_cost || "",
          urgency_cost: r.data.urgency_cost || "",
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings/extras-defaults`, {
        sponda_cost: parseFloat(values.sponda_cost) || 0,
        camion_cost: parseFloat(values.camion_cost) || 0,
        furgone_cost: parseFloat(values.furgone_cost) || 0,
        urgency_cost: parseFloat(values.urgency_cost) || 0,
      }, h);
      toast.success("Tariffe di default salvate");
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="bg-white border border-zinc-200 rounded-sm p-6" data-testid="section-extras-defaults">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-sm bg-orange-100 flex items-center justify-center flex-shrink-0">
          <Truck size={18} className="text-[#FF5200]" />
        </div>
        <div>
          <h3 className="font-heading text-sm font-bold uppercase tracking-widest text-zinc-800">Tariffe Servizi Extra</h3>
          <p className="text-sm text-zinc-500 mt-1">
            Prezzi predefiniti applicati automaticamente quando spunti un servizio extra nella creazione di una spedizione o ritiro. Puoi sempre modificarli caso per caso.
          </p>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-zinc-500"><Loader2 size={14} className="inline animate-spin mr-2" /> Caricamento...</p>
      ) : (
        <div className="space-y-3">
          {[
            { key: "sponda_cost", label: "Servizio sponda (€)", placeholder: "es. 15.00" },
            { key: "camion_cost", label: "Camion completo (€)", placeholder: "es. 250.00" },
            { key: "furgone_cost", label: "Furgone completo (€)", placeholder: "es. 180.00" },
            { key: "urgency_cost", label: "Supplemento urgenza (€)", placeholder: "es. 30.00" },
          ].map((f) => (
            <div key={f.key} className="flex items-center justify-between gap-4">
              <label className="text-sm text-zinc-700 font-medium">{f.label}</label>
              <input
                type="number" step="0.01" min="0"
                value={values[f.key]}
                onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                placeholder={f.placeholder}
                className="w-40 h-9 px-3 border border-zinc-300 rounded-sm text-sm focus:border-[#FF5200] outline-none"
                data-testid={`extras-default-${f.key}`}
              />
            </div>
          ))}
          <button
            onClick={save}
            disabled={saving}
            className="mt-2 inline-flex items-center gap-2 bg-[#FF5200] text-white px-4 py-2 text-sm font-bold uppercase tracking-wide rounded-sm hover:bg-[#e04800] disabled:opacity-50"
            data-testid="extras-defaults-save-btn"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Salva Tariffe
          </button>
        </div>
      )}
    </section>
  );
}

function ChangePasswordSection() {
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [loading, setLoading] = useState(false);

  const token = localStorage.getItem("mv_admin_token");
  const authHeaders = () => ({ headers: { Authorization: `Bearer ${token}` } });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentPw || !newPw || !confirmPw) { toast.error("Compila tutti i campi"); return; }
    if (newPw.length < 6) { toast.error("La nuova password deve avere almeno 6 caratteri"); return; }
    if (newPw !== confirmPw) { toast.error("Le password non coincidono"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/admin/change-password`, { current_password: currentPw, new_password: newPw }, authHeaders());
      toast.success("Password admin aggiornata con successo!");
      setCurrentPw(""); setNewPw(""); setConfirmPw("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel cambio password");
    } finally { setLoading(false); }
  };

  const inputCls = "w-full h-11 px-3 border-2 border-zinc-200 rounded-sm focus:border-[#FF5200] outline-none text-sm font-body";
  const lblCls = "block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1 font-heading";

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="flex items-center justify-center w-10 h-10 bg-[#09090B] rounded-sm">
          <Lock size={18} className="text-[#FF5200]" />
        </div>
        <div>
          <h3 className="text-lg font-black tracking-tight text-[#09090B] font-heading" data-testid="admin-change-password-title">CAMBIA PASSWORD</h3>
          <p className="text-xs text-zinc-500">Minimo 6 caratteri</p>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="admin-change-password-form">
        <div>
          <label className={lblCls}>Password attuale *</label>
          <input type="password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)}
            className={inputCls} placeholder="Password attuale" data-testid="admin-current-password-input" />
        </div>
        <div>
          <label className={lblCls}>Nuova password *</label>
          <input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)}
            className={inputCls} placeholder="Nuova password (min 6 caratteri)" data-testid="admin-new-password-input" />
        </div>
        <div>
          <label className={lblCls}>Conferma nuova password *</label>
          <input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)}
            className={inputCls} placeholder="Ripeti nuova password" data-testid="admin-confirm-password-input" />
        </div>
        <button type="submit" disabled={loading}
          className="inline-flex items-center gap-2 bg-[#FF5200] text-white px-6 py-3 font-bold uppercase tracking-wide rounded-sm hover:bg-[#e64a00] transition-colors text-sm font-heading disabled:opacity-50"
          data-testid="admin-change-password-submit-btn">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />}
          Aggiorna Password
        </button>
      </form>
    </div>
  );
}



function FactoryResetSection() {
  const [scope, setScope] = useState("test_only");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const token = localStorage.getItem("mv_admin_token");
  const authHeaders = () => ({ headers: { Authorization: `Bearer ${token}` } });

  const scopeLabels = {
    test_only: "Solo dati di test (sicuro)",
    operational: "Reset dati operativi",
    full: "Reset totale (azzeramento)",
  };

  const scopeDescriptions = {
    test_only: "Cancella solo record con parole chiave test: TEST, smoke, iteration, regression. Consigliato per prima pulizia dopo deploy.",
    operational: "Cancella: spedizioni, ritiri, distinte, magazzino (richieste/movimenti/ordini/inventario), preventivi, contatti, notifiche, audit log, timesheet, assenze autisti. MANTIENE: clienti, autisti, posizioni magazzino, recensioni, blog.",
    full: "Cancella TUTTO tranne admin, blog e recensioni. Include anche clienti, autisti e posizioni. Da usare solo per ripartire da zero.",
  };

  const scopeColors = {
    test_only: "border-amber-200 bg-amber-50",
    operational: "border-red-200 bg-red-50",
    full: "border-red-400 bg-red-100",
  };

  const handleReset = async () => {
    if (!password) { toast.error("Inserisci la password admin per confermare"); return; }
    const confirmMsg = scope === "full"
      ? "ATTENZIONE: stai per cancellare TUTTI i dati inclusi clienti e autisti. Questa operazione e IRREVERSIBILE. Continuare?"
      : scope === "operational"
      ? "Stai per cancellare tutti i dati operativi (spedizioni, magazzino, etc). Clienti e autisti restano. Continuare?"
      : "Stai per cancellare solo i dati marcati come test. Continuare?";
    if (!window.confirm(confirmMsg)) return;

    setLoading(true);
    setResult(null);
    try {
      const r = await axios.post(`${API}/admin/factory-reset`, { confirm_password: password, scope }, authHeaders());
      setResult(r.data);
      toast.success("Reset completato");
      setPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore reset");
    } finally { setLoading(false); }
  };

  const inputCls = "w-full h-11 px-3 border-2 border-zinc-200 rounded-sm focus:border-red-500 outline-none text-sm font-body";
  const lblCls = "block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1 font-heading";

  return (
    <div className="border-2 border-red-200 rounded-sm p-6 bg-red-50/30" data-testid="factory-reset-section">
      <div className="flex items-center gap-3 mb-6">
        <div className="flex items-center justify-center w-10 h-10 bg-red-600 rounded-sm">
          <AlertTriangle size={18} className="text-white" />
        </div>
        <div>
          <h3 className="text-lg font-black tracking-tight text-red-700 font-heading">DANGER ZONE — RESET DATI</h3>
          <p className="text-xs text-zinc-500">Azioni irreversibili. Usa con cautela.</p>
        </div>
      </div>

      <div className="space-y-3 mb-4">
        {Object.entries(scopeLabels).map(([k, label]) => (
          <label key={k} className={`block border-2 rounded-sm p-3 cursor-pointer transition-colors ${scope === k ? scopeColors[k] : "border-zinc-200 bg-white hover:border-zinc-300"}`}>
            <div className="flex items-start gap-3">
              <input type="radio" name="reset-scope" value={k} checked={scope === k}
                onChange={e => setScope(e.target.value)} className="mt-1" data-testid={`reset-scope-${k}`} />
              <div className="flex-1">
                <p className="text-sm font-bold text-[#09090B]">{label}</p>
                <p className="text-xs text-zinc-600 mt-1 leading-relaxed">{scopeDescriptions[k]}</p>
              </div>
            </div>
          </label>
        ))}
      </div>

      <div className="mb-4">
        <label className={lblCls}>Conferma con password admin *</label>
        <input type="password" value={password} onChange={e => setPassword(e.target.value)}
          className={inputCls} placeholder="Inserisci la tua password admin" data-testid="reset-password-input" />
      </div>

      <button onClick={handleReset} disabled={loading || !password}
        className="inline-flex items-center gap-2 bg-red-600 text-white px-6 py-3 font-bold uppercase tracking-wide rounded-sm hover:bg-red-700 transition-colors text-sm font-heading disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="factory-reset-btn">
        {loading ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
        Esegui Reset
      </button>

      {result && (
        <div className="mt-4 p-3 bg-white border border-zinc-200 rounded-sm" data-testid="reset-result">
          <p className="text-xs font-bold uppercase text-zinc-500 mb-2">Record eliminati ({result.scope}):</p>
          <ul className="text-xs font-mono space-y-0.5">
            {Object.entries(result.deleted || {}).map(([coll, count]) => (
              <li key={coll} className={count > 0 ? "text-red-600" : "text-zinc-400"}>
                {coll}: {count}
              </li>
            ))}
          </ul>
          {Object.values(result.deleted || {}).every(v => v === 0) && (
            <p className="text-xs text-zinc-500 italic">Nessun record corrispondente trovato.</p>
          )}
        </div>
      )}
    </div>
  );
}
